import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import Modal from "common/modal/component/modalBase";
import {
  BodyDiv,
  ButtonsDiv,
  CenteredTitle,
  SubscriptionPlaceholder,
} from "./infoModalStyle";
import { MAX_CLAIM_NUMBER_THRESHOLD } from "utils/numberConstants";
import { BlueWhiteBtn } from "common/button";
import TickIconUrl from "statics/img/icon-tick-green.svg";
import RedirectIcon from "statics/img/icon-redirect.svg";
import { Redirect } from "react-router-dom";

/**
 * @typedef IListItem
 * @type {object}
 * @property {string} [icon] - The icon to display on the left side.
 * @property {string} title - The topic to be bolded.
 * @property {string} description - The string to display below the respective title.
 */

/**
 * @typedef IDescription
 * @type {object}
 * @property {React.JSX.Element} [mainTitle] - The title to display below the icon.
 * @property {React.JSX.Element} [subTitle] - The subtitle to display below the main title.
 * @property {string} buttonText - Text to put on the button.
 * @property {IListItem[]} listItem - The setting to setup the list of
 */

/**
 * @typedef ISetting
 * @type {object}
 * @property {string} title - The main title to display on the window.
 * @property {IDescription} description - The setting that is used to set items on the body.
 */

/**
 * @typedef TSubscriptionPlan
 * @type {"choose_plan"|"upgrade_plan"}
 */

/**
 * @type {IListItem[]}
 */
const defaultCommonListItem = [
  {
    icon: TickIconUrl,
    title: <b>Make more profitable use of your day.</b>,
    description: (
      <span>
        With PayLab, all payment claims are standardised for easy processing and
        certification. It's simple to spot and resolve discrepancies, and
        there's no manual data entry. This saves you time and boosts
        profitability.
      </span>
    ),
  },
  {
    icon: TickIconUrl,
    title: <b>You know it's compliant.</b>,
    description: (
      <span>
        PayLab ensures compliance for you and your subcontractors, adhering to
        the Construction Contracts Act 2002 and the Retention Money Amendment
        Act 2023. Stay guided and informed throughout the entire process.
      </span>
    ),
  },
  {
    icon: TickIconUrl,
    title: <b>Easy to monitor your projects' progress.</b>,
    description: (
      <span>
        PayLab centralises all project claims and certificates in an
        easy-to-read platform, offering real-time updates. Efficiently manage
        retentions, track claims and payments, and get automatic prompts to
        address uncertified claims before issues arise.
      </span>
    ),
  },
];

/**
 * Generic component to use to display subscription recommendation.
 *
 * @param {object} props
 * @param {boolean} [props.isOpen] - Prop to render the modal on the page.
 * @param {() => void} [props.closeModal] - Function to handle clicking close icon. Leave undefined to remove close icon.
 * @param {() => [string, object]} [props.handleClick] - Function to trigger when clicking the button below the body of the modal. If the string returned is not empty, function as if void. Ideally returns the list with the same arguments used for react-router redirect component.
 * @param {string} [props.buttonPlaceholderText] - Text to display if handleClick is undefined.
 * @param {TSubscriptionPlan} props.infoType - Value to indicate what to display.
 * @returns {React.JSX.Element}
 *
 * @example
 * <SubscriptionInfoModal
 *   infoType={"choose_plan"}
 *   isOpen
 *   closeModal={() => console.log("close")}
 *   handleClick={() => console.log("clicked")}
 * />
 */
const SubscriptionInfoModal = (props) => {
  const {
    infoType,
    isOpen,
    buttonPlaceholderText = "",
    planButtonText = "View Plans",
    errorMessage = "You've reached the limits of your current plan. Upgrade now for even more active contracts.",
    closeModal,
    handleClick,
    getAccountStatistics,
    accountStatistics,
    noSubscriptionTerms,
  } = props;

  const noSubscriptionTermsObject = noSubscriptionTerms?.toJS() || {};

  const [addRedirectComponent, setAddRedirectComponent] = useState([]);
  const [commonListItem, setCommonListItem] = useState([]);

  useEffect(() => {
    getAccountStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const {
      claim_contract_count = 0,
      subscription_claim_limit,
      cert_contract_count = 0,
      subscription_cert_limit = 0,
    } = accountStatistics || {};
    if (accountStatistics !== undefined) {
      if (
        subscription_claim_limit !== null &&
        subscription_cert_limit !== null
      ) {
        setCommonListItem([
          {
            title: <b>You are using:</b>,
            description: (
              <ul>
                <li>
                  <b>{claim_contract_count || 0}</b> active main contracts (
                  {subscription_claim_limit > MAX_CLAIM_NUMBER_THRESHOLD
                    ? "Unlimited"
                    : `Limit ${subscription_claim_limit}`}
                  )
                </li>
                <li>
                  <b>{cert_contract_count || 0}</b> active sub-contracts (Limit{" "}
                  {subscription_cert_limit || 0})
                </li>
              </ul>
            ),
          },
          {
            title: <b>You can:</b>,
            description: (
              <ul>
                <li>Upgrade your subscription</li>
                <li>Archive contracts that you are not using</li>
              </ul>
            ),
          },
        ]);
      } else {
        setCommonListItem(defaultCommonListItem);
      }
    }
  }, [accountStatistics]);

  useEffect(() => {
    if (addRedirectComponent.length > 0) {
      setAddRedirectComponent([]);
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRedirectComponent.length]);

  const { title, description } = useMemo(() => {
    /**
     * @type {ISetting}
     */
    const setting = {
      title: "",
      description: {
        mainTitle: <></>,
        subTitle: <></>,
        buttonText: "",
        listItem: [],
      },
    };

    switch (infoType) {
      case "choose_plan":
        const commonListItemCopy = _.cloneDeep(commonListItem);
        (noSubscriptionTermsObject?.items || []).forEach((item, index) => {
          const { header, description } = item;

          if (!header || !description) {
            return;
          }

          if (index >= commonListItemCopy.length) {
            commonListItemCopy.push({
              title: <b>{header}</b>,
              description: <span>{description}</span>,
            });
            return;
          }

          commonListItemCopy[index].title = <b>{header}</b>;
          commonListItemCopy[index].description = <span>{description}</span>;
        });
        setting.title = "Unlock the power of PayLab";
        setting.description = {
          mainTitle: <b className="main-title">Choose a plan to get started</b>,
          subTitle: (
            <span>
              {noSubscriptionTermsObject?.sub_title ||
                "PayLab requires a paid subscription to be able to claim or certify progress payments."}
            </span>
          ),
          buttonText: "Choose a Plan",
          listItem: commonListItemCopy,
        };
        break;
      case "upgrade_plan":
        setting.title = "Upgrade your plan";
        setting.description = {
          mainTitle: (
            <b className="main-title">
              You've reached your current plans limits
            </b>
          ),
          subTitle: <span>{errorMessage}</span>,
          buttonText: planButtonText,
          listItem: commonListItem,
        };
        break;
    }

    return setting;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoType, commonListItem, noSubscriptionTermsObject]);

  const { mainTitle, subTitle, buttonText, listItem } = description;

  const handleClickWithLink = () => {
    if (!handleClick) {
      return;
    }
    const redirectItems = handleClick();
    if (redirectItems[1] === "external") {
      window.open(redirectItems[0], "_blank");
      return;
    }
    setAddRedirectComponent(redirectItems);
  };

  if (addRedirectComponent.length > 0) {
    return (
      <Redirect
        push
        to={{ ...addRedirectComponent[1], pathname: addRedirectComponent[0] }}
      />
    );
  }

  return (
    <Modal
      title={<CenteredTitle>{title}</CenteredTitle>}
      open={isOpen}
      divider={false}
      handleClose={closeModal}
    >
      <BodyDiv>
        <div className="subscription-image" />
        {mainTitle}
        {subTitle}
        {listItem.length > 0 && (
          <div className="list-box">
            {listItem.map(({ icon, title, description }, index) => {
              return (
                <div key={`${index}-${title}`} className="list-item">
                  {title}
                  <br />
                  <div className="description">{description}</div>
                </div>
              );
            })}
          </div>
        )}
      </BodyDiv>
      <ButtonsDiv>
        {handleClick ? (
          <BlueWhiteBtn
            width="100%"
            onClick={handleClickWithLink}
            label={buttonText}
          />
        ) : (
          <SubscriptionPlaceholder>
            <span>{buttonPlaceholderText}</span>
          </SubscriptionPlaceholder>
        )}
        <a
          className="pricing-redirect"
          href="https://paylab.asbuiltvault.com/pricing"
          rel="noreferrer"
          target="_blank"
        >
          <b>Learn more</b>
          <img src={RedirectIcon} />
        </a>
      </ButtonsDiv>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    accountStatistics: state.getIn(["headers", "accountStatistics"]),
    noSubscriptionTerms: state.getIn([
      "config",
      "accountConfig",
      "pages",
      "settings",
      "no_subscription_modal",
    ]),
  };
};

export default connect(mapStateToProps, null)(SubscriptionInfoModal);
